import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Tooltip,

} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { fetchJson } from '../../../utils/fetchJson';
import useScopeAutoRefresh from '../../../hooks/useScopeAutoRefresh';
import AppLoader from '../../../components/AppLoader';

const formatMinutes = (totalMinutes) => {
  const mins = Math.max(0, parseInt(totalMinutes || 0, 10) || 0);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${String(m).padStart(2, '0')}m`;
};

const minutesToHHMM = (totalMinutes) => {
  const mins = Math.max(0, parseInt(totalMinutes || 0, 10) || 0);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const hhmmToMinutes = (hhmm) => {
  const s = String(hhmm || '').trim();
  if (!s) return 0;
  const m = /^([0-9]{1,3}):([0-5][0-9])$/.exec(s);
  if (!m) return 0;
  const h = parseInt(m[1], 10) || 0;
  const mm = parseInt(m[2], 10) || 0;
  return Math.max(0, h * 60 + mm);
};

const computeSuspensionDays = ({ suspensionStartDate, suspensionEndDate, suspension }) => {
  try {
    const start = suspensionStartDate ? new Date(suspensionStartDate) : null;
    const end = suspensionEndDate ? new Date(suspensionEndDate) : null;
    if (start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const ms = end.getTime() - start.getTime();
      const days = Math.floor(ms / (24 * 60 * 60 * 1000)) + 1;
      return Math.max(0, days);
    }
  } catch (_) {}
  const n = Number(suspension || 0);
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0;
};

const StudentsDisciplinePage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [students, setStudents] = useState([]);
  const [sequenceWindows, setSequenceWindows] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [selectedSequenceNo, setSelectedSequenceNo] = useState('');
  const [searchStudentId, setSearchStudentId] = useState('');
  const [disciplineRecords, setDisciplineRecords] = useState([]);
  const [summaryStats, setSummaryStats] = useState(null);
  const [sequenceEvaluationId, setSequenceEvaluationId] = useState(null);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [activePhaseCode, setActivePhaseCode] = useState('');

  const isNurPriPhase = String(activePhaseCode || '').trim().toLowerCase() === 'nur_pri';

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [dialogMode, setDialogMode] = useState('create');
  const [editTotals, setEditTotals] = useState({ absTotal: null, lateTotal: null });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);

  useEffect(() => {
    if (!openDialog) return;
    if (dialogMode !== 'edit') return;
    if (!editingRecord) return;

    const nextClassId = editingRecord.classId || selectedClass;
    if (nextClassId) setSelectedClass(nextClassId);

    const baseJustAbs = editingRecord?.manual?.justifiedAbsenceMinutes ?? editingRecord?.justifiedAbsenceMinutes ?? 0;
    const baseUnjAbs = editingRecord?.manual?.unjustifiedAbsenceMinutes ?? editingRecord?.unjustifiedAbsenceMinutes ?? 0;
    const absTotal = Math.max(0, (Number(baseJustAbs) || 0) + (Number(baseUnjAbs) || 0));

    const hasManualLate =
      editingRecord?.manual?.justifiedLateness != null ||
      editingRecord?.manual?.unjustifiedLateness != null;
    const baseJustLate = hasManualLate ? (editingRecord?.manual?.justifiedLateness ?? 0) : 0;
    const baseUnjLate = hasManualLate ? (editingRecord?.manual?.unjustifiedLateness ?? 0) : (editingRecord?.lateMinutes ?? 0);
    const lateTotal = Math.max(0, (Number(baseJustLate) || 0) + (Number(baseUnjLate) || 0));
    setEditTotals({ absTotal, lateTotal });

    setFormData({
      studentId: editingRecord.studentId,
      classId: nextClassId,
      academicYearId: selectedAcademicYear,
      termId: Math.ceil((parseInt(selectedSequenceNo, 10) || 1) / 2),
      justifiedAbsenceMinutes: minutesToHHMM(
        editingRecord?.manual?.justifiedAbsenceMinutes ?? editingRecord?.justifiedAbsenceMinutes ?? 0
      ),
      unjustifiedAbsenceMinutes: minutesToHHMM(
        editingRecord?.manual?.unjustifiedAbsenceMinutes ?? editingRecord?.unjustifiedAbsenceMinutes ?? 0
      ),
      justifiedLatenessMinutes: minutesToHHMM(baseJustLate),
      unjustifiedLatenessMinutes: minutesToHHMM(baseUnjLate),
      warnings: editingRecord?.manual?.warnings || 0,
      suspensionEnabled: !!(
        editingRecord?.manual?.suspensionStartDate ||
        editingRecord?.manual?.suspensionEndDate ||
        (Number(editingRecord?.manual?.suspension || 0) > 0)
      ),
      suspensionStartDate: editingRecord?.manual?.suspensionStartDate || '',
      suspensionEndDate: editingRecord?.manual?.suspensionEndDate || '',
      dismissal: editingRecord?.manual?.exclusion || false
    });
  }, [openDialog, dialogMode, editingRecord, selectedAcademicYear, selectedSequenceNo, selectedClass]);

  // Form state
  const [formData, setFormData] = useState({
    studentId: '',
    classId: '',
    academicYearId: '',
    justifiedAbsenceMinutes: '00:00',
    unjustifiedAbsenceMinutes: '00:00',
    justifiedLatenessMinutes: '00:00',
    unjustifiedLatenessMinutes: '00:00',
    warnings: 0,
    suspensionEnabled: false,
    suspensionStartDate: '',
    suspensionEndDate: '',
    dismissal: false
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedAcademicYear) {
      fetchSequenceWindows();
    }
  }, [selectedAcademicYear]);

  useEffect(() => {
    if (selectedSequenceNo && (selectedClass || searchStudentId)) {
      fetchDisciplineSummary();
    }
  }, [selectedClass, searchStudentId, selectedSequenceNo]);

  const fetchInitialData = async () => {
    try {
      setError(null);
      setLoading(true);
      // Context + cache keys
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      setActivePhaseCode(String(phaseCode || '').trim());
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || '';
      const ctxKey = `${schoolId}|${phaseId || phaseCode || '-'}|${ayId || '-'}`;
      const key = (name) => `studDisc:${name}:${ctxKey}`;
      let prefilled = false;
      try { const r = localStorage.getItem(key('classes')); if (r) { const v = JSON.parse(r); if (Array.isArray(v)) { setClasses(v); if (v.length) { setSelectedClass(v[0]?.id || ''); prefilled = true; }}}} catch(_) {}
      try { const r = localStorage.getItem(key('years')); if (r) { const v = JSON.parse(r); if (Array.isArray(v)) { setAcademicYears(v); if (v.length) { setSelectedAcademicYear(v[0]?.id || ''); prefilled = true; }}}} catch(_) {}
      try { const r = localStorage.getItem(key('students')); if (r) { const v = JSON.parse(r); if (Array.isArray(v)) { setStudents(v); if (v.length) prefilled = true; }}} catch(_) {}

      // If we showed cached data, don't block UI
      if (prefilled) setLoading(false);
      let maxGuard; try { maxGuard = setTimeout(() => setLoading(false), 2000); } catch(_) {}

      // Live fetch in parallel
      const [classesData, academicYearsData, studentsData] = await Promise.all([
        fetchJson('/api/classes'),
        fetchJson('/api/global-academic-years'),
        fetchJson('/api/students')
      ]);
      setClasses(classesData);
      if (classesData?.length > 0) setSelectedClass(classesData[0].id);
      setAcademicYears(academicYearsData);
      if (academicYearsData?.length > 0) {
        const nextAyId = academicYearsData[0].id;
        setSelectedAcademicYear(nextAyId);
        // Persist so fetchJson injects correct X-Academic-Year-Id
        try {
          if (nextAyId) localStorage.setItem(ayKey, String(nextAyId));
          try { window.dispatchEvent(new CustomEvent('app:ay:changed', { detail: { id: Number(nextAyId) } })); } catch (_) {}
        } catch (_) {}
      }
      setStudents(studentsData);
      try { localStorage.setItem(key('classes'), JSON.stringify(classesData)); } catch(_) {}
      try { localStorage.setItem(key('years'), JSON.stringify(academicYearsData)); } catch(_) {}
      try { localStorage.setItem(key('students'), JSON.stringify(studentsData)); } catch(_) {}
      setLoading(false);
      try { clearTimeout(maxGuard); } catch(_) {}
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setError(t('studentsDisciplinePage.errors.fetchInitialData', { message: error?.message || '' }));
      enqueueSnackbar(t('studentsDisciplinePage.errors.fetchInitialDataSnack'), { variant: 'error' });
      setLoading(false);
    }
  };

  const fetchSequenceWindows = async () => {
    try {
      setError(null);
      const data = await fetchJson('/api/student-discipline/summary/sequence/windows');
      const list = Array.isArray(data?.windows) ? data.windows : [];
      setSequenceWindows(list);
      if (list.length) {
        const first = String(list[0]?.sequenceNo || '1');
        if (!selectedSequenceNo) setSelectedSequenceNo(first);
      } else {
        // Strict academic calendar behavior: no window => no sequence selection
        setSelectedSequenceNo('');
      }
    } catch (e) {
      console.error('Error fetching sequence windows:', e);
      setError(t('studentsDisciplinePage.errors.fetchSequenceWindows', { message: e?.message || '' }));
      setSelectedSequenceNo('');
    }
  };

  const fetchDisciplineSummary = async () => {
    try {
      setError(null);
      setInfoMessage(null);
      setSequenceEvaluationId(null);
      if (!selectedSequenceNo) {
        setError(t('studentsDisciplinePage.errors.sequenceRequired'));
        return;
      }
      const params = new URLSearchParams();
      if (selectedClass) params.set('classId', selectedClass);
      if (searchStudentId) params.set('studentId', searchStudentId);
      if (selectedSequenceNo) params.set('sequenceNo', selectedSequenceNo);
      const data = await fetchJson(`/api/student-discipline/summary/sequence?${params.toString()}`);
      setSequenceEvaluationId(data?.evaluationId != null ? Number(data.evaluationId) : null);
      const rawRows = Array.isArray(data?.rows) ? data.rows : [];
      // Overlay manual edits onto the display rows.
      // Attendance-derived minutes are still available on the base fields, but when manual values exist
      // we want the UI to reflect what was edited/saved.
      const rows = rawRows.map((r) => {
        if (!r?.manual) return r;
        return {
          ...r,
          justifiedAbsenceMinutes:
            r.manual.justifiedAbsenceMinutes != null
              ? Number(r.manual.justifiedAbsenceMinutes) || 0
              : (r.justifiedAbsenceMinutes || 0),
          unjustifiedAbsenceMinutes:
            r.manual.unjustifiedAbsenceMinutes != null
              ? Number(r.manual.unjustifiedAbsenceMinutes) || 0
              : (r.unjustifiedAbsenceMinutes || 0)
        };
      });
      if (data?.message) setInfoMessage(String(data.message));
      setDisciplineRecords(rows);
      setSummaryStats({
        totalAbsenceMinutes: rows.reduce((s, r) => s + (r.unjustifiedAbsenceMinutes || 0), 0),
        totalLateMinutes: rows.reduce((s, r) => {
          const hasManualLate = r?.manual?.justifiedLateness != null || r?.manual?.unjustifiedLateness != null;
          const unjustified = hasManualLate ? (r?.manual?.unjustifiedLateness || 0) : (r?.lateMinutes || 0);
          return s + (unjustified || 0);
        }, 0),
        totalWarnings: rows.reduce((s, r) => s + (r?.manual?.warnings || 0), 0),
        totalExclusions: rows.reduce((s, r) => s + (r?.manual?.exclusion ? 1 : 0), 0)
      });
    } catch (error) {
      console.error('Error fetching discipline summary:', error);
      setError(t('studentsDisciplinePage.errors.fetchSummary', { message: error?.message || '' }));
      enqueueSnackbar(t('studentsDisciplinePage.errors.fetchSummarySnack'), { variant: 'error' });
    }
  };

  // Auto-refresh AFTER all loader functions are defined
  useScopeAutoRefresh([fetchInitialData, () => {
    if (selectedSequenceNo && (selectedClass || searchStudentId)) {
      fetchDisciplineSummary();
    }
  }]);

  const handleOpenDialog = (mode, record = null) => {
    setDialogMode(mode);
    if (mode === 'edit' && record) {
      setEditingRecord(record);

      const baseJustAbs = record?.manual?.justifiedAbsenceMinutes ?? record?.justifiedAbsenceMinutes ?? 0;
      const baseUnjAbs = record?.manual?.unjustifiedAbsenceMinutes ?? record?.unjustifiedAbsenceMinutes ?? 0;
      const absTotal = Math.max(0, (Number(baseJustAbs) || 0) + (Number(baseUnjAbs) || 0));

      const hasManualLate = record?.manual?.justifiedLateness != null || record?.manual?.unjustifiedLateness != null;
      const baseJustLate = hasManualLate ? (record?.manual?.justifiedLateness ?? 0) : 0;
      const baseUnjLate = hasManualLate ? (record?.manual?.unjustifiedLateness ?? 0) : (record?.lateMinutes ?? 0);
      const lateTotal = Math.max(0, (Number(baseJustLate) || 0) + (Number(baseUnjLate) || 0));

      setEditTotals({ absTotal, lateTotal });
      setFormData({
        studentId: record.studentId,
        classId: record.classId,
        academicYearId: selectedAcademicYear,
        termId: Math.ceil((parseInt(selectedSequenceNo, 10) || 1) / 2),
        justifiedAbsenceMinutes: minutesToHHMM(record?.manual?.justifiedAbsenceMinutes ?? record?.justifiedAbsenceMinutes ?? 0),
        unjustifiedAbsenceMinutes: minutesToHHMM(record?.manual?.unjustifiedAbsenceMinutes ?? record?.unjustifiedAbsenceMinutes ?? 0),
        justifiedLatenessMinutes: minutesToHHMM(baseJustLate),
        unjustifiedLatenessMinutes: minutesToHHMM(baseUnjLate),
        warnings: record?.manual?.warnings || 0,
        suspensionEnabled: !!(
          record?.manual?.suspensionStartDate ||
          record?.manual?.suspensionEndDate ||
          (Number(record?.manual?.suspension || 0) > 0)
        ),
        suspensionStartDate: record?.manual?.suspensionStartDate || '',
        suspensionEndDate: record?.manual?.suspensionEndDate || '',
        dismissal: record?.manual?.exclusion || false
      });
    } else {
      setEditingRecord(null);
      setEditTotals({ absTotal: null, lateTotal: null });
      setFormData({
        studentId: '',
        classId: selectedClass,
        academicYearId: selectedAcademicYear,
        termId: Math.ceil((parseInt(selectedSequenceNo, 10) || 1) / 2),
        justifiedAbsenceMinutes: '00:00',
        unjustifiedAbsenceMinutes: '00:00',
        justifiedLatenessMinutes: '00:00',
        unjustifiedLatenessMinutes: '00:00',
        warnings: 0,
        suspensionEnabled: false,
        suspensionStartDate: '',
        suspensionEndDate: '',
        dismissal: false
      });
    }
    setOpenDialog(true);
  };

  const handleOpenViewDialog = (record) => {
    setViewRecord(record || null);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewRecord(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRecord(null);
    setEditTotals({ absTotal: null, lateTotal: null });
    setFormData({
      studentId: '',
      classId: selectedClass,
      academicYearId: selectedAcademicYear,
      termId: Math.ceil((parseInt(selectedSequenceNo, 10) || 1) / 2),
      justifiedAbsenceMinutes: '00:00',
      unjustifiedAbsenceMinutes: '00:00',
      justifiedLatenessMinutes: '00:00',
      unjustifiedLatenessMinutes: '00:00',
      warnings: 0,
      suspensionEnabled: false,
      suspensionStartDate: '',
      suspensionEndDate: '',
      dismissal: false
    });
  };

  const rebalanceMinutesPair = ({ total, nextValueHHMM, side }) => {
    if (total == null) return null;
    const s = String(nextValueHHMM || '').trim();
    if (!/^([0-9]{1,3}):([0-5][0-9])$/.test(s)) return null;
    const t = Math.max(0, Number(total) || 0);
    const vRaw = hhmmToMinutes(nextValueHHMM);
    const v = Math.max(0, Math.min(vRaw, t));
    const other = Math.max(t - v, 0);
    if (side === 'justified') {
      return {
        justified: minutesToHHMM(v),
        unjustified: minutesToHHMM(other)
      };
    }
    return {
      justified: minutesToHHMM(other),
      unjustified: minutesToHHMM(v)
    };
  };

  const handleSubmit = async () => {
    try {
      const jAbsMin = hhmmToMinutes(formData.justifiedAbsenceMinutes);
      const uAbsMin = hhmmToMinutes(formData.unjustifiedAbsenceMinutes);
      const jLateMin = hhmmToMinutes(formData.justifiedLatenessMinutes);
      const uLateMin = hhmmToMinutes(formData.unjustifiedLatenessMinutes);

      let effectiveEvaluationId = sequenceEvaluationId;
      if (!effectiveEvaluationId && selectedSequenceNo) {
        try {
          const params = new URLSearchParams();
          const cId = formData.classId || selectedClass;
          if (cId) params.set('classId', cId);
          if (formData.studentId) params.set('studentId', formData.studentId);
          params.set('sequenceNo', selectedSequenceNo);
          const summary = await fetchJson(`/api/student-discipline/summary/sequence?${params.toString()}`);
          effectiveEvaluationId = summary?.evaluationId != null ? Number(summary.evaluationId) : null;
          setSequenceEvaluationId(effectiveEvaluationId);
        } catch (_) {
          effectiveEvaluationId = null;
        }
      }

      const payload = {
        studentId: formData.studentId,
        classId: formData.classId || selectedClass,
        globalAcademicYearId: formData.academicYearId || selectedAcademicYear,
        termId: Math.ceil((parseInt(selectedSequenceNo, 10) || 1) / 2),
        evaluationId: effectiveEvaluationId,
        justifiedAbsenceMinutes: jAbsMin,
        unjustifiedAbsenceMinutes: uAbsMin,
        totalAbsenceMinutes: jAbsMin + uAbsMin,
        justifiedLateness: jLateMin,
        unjustifiedLateness: uLateMin,
        warnings: formData.warnings,
        suspensionStartDate: formData.suspensionEnabled ? (formData.suspensionStartDate || null) : null,
        suspensionEndDate: formData.suspensionEnabled ? (formData.suspensionEndDate || null) : null,
        exclusion: !!formData.dismissal
      };

      const manualRecordId = editingRecord?.manualRecordId || editingRecord?.manual?.id || null;
      if (dialogMode === 'edit' && manualRecordId) {
        await fetchJson(`/api/student-discipline/${manualRecordId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: payload
        });
      } else {
        await fetchJson('/api/student-discipline', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload
        });
      }

      enqueueSnackbar(
        dialogMode === 'edit'
          ? t('studentsDisciplinePage.snack.updated')
          : t('studentsDisciplinePage.snack.saved'),
        { variant: 'success' }
      );
      handleCloseDialog();
      try {
        await fetchDisciplineSummary();
      } catch (_) {}
    } catch (error) {
      console.error('Error saving discipline record:', error);
      enqueueSnackbar(t('studentsDisciplinePage.errors.saveRecord', { message: error?.message || String(error) }), { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      if (!recordToDelete?.manualRecordId) {
        enqueueSnackbar(t('studentsDisciplinePage.warnings.noManualRecord'), { variant: 'warning' });
        setDeleteConfirmOpen(false);
        setRecordToDelete(null);
        return;
      }

      await fetchJson(`/api/student-discipline/${recordToDelete.manualRecordId}`, { method: 'DELETE' });
      
      enqueueSnackbar(t('studentsDisciplinePage.snack.deleted'), { variant: 'success' });
      setDeleteConfirmOpen(false);
      setRecordToDelete(null);
      fetchDisciplineSummary();
    } catch (error) {
      console.error('Error deleting discipline record:', error);
      enqueueSnackbar(t('studentsDisciplinePage.errors.deleteRecord', { message: error?.message || '' }), { variant: 'error' });
    }
  };

  // Only block if nothing to show yet
  if (loading && (!classes || classes.length === 0)) {
    return (
      <Box sx={{ p: 3 }}>
        <AppLoader variant="circular3" size={60} centered minHeight="60vh" messages={[]} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('studentsDisciplinePage.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('create')}
        >
          {t('studentsDisciplinePage.actions.addRecord')}
        </Button>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {sequenceWindows.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('studentsDisciplinePage.warnings.noSequenceWindow')}
          {activePhaseCode ? t('studentsDisciplinePage.warnings.phaseSuffix', { phase: activePhaseCode }) : ''}
        </Alert>
      )}

      {infoMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {infoMessage}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('studentsDisciplinePage.filters.class')}</InputLabel>
              <Select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                label={t('studentsDisciplinePage.filters.class')}
              >
                <MenuItem value="">{t('studentsDisciplinePage.filters.allClasses')}</MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.name}{cls.section ? ` ${cls.section}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('studentsDisciplinePage.filters.academicYear')}</InputLabel>
              <Select
                value={selectedAcademicYear}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedAcademicYear(val);
                  // Persist so fetchJson injects correct X-Academic-Year-Id
                  const sid = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
                  const key = sid ? `${sid}:currentAcademicYearId` : 'currentAcademicYearId';
                  if (val) localStorage.setItem(key, String(val));
                  try { window.dispatchEvent(new CustomEvent('app:ay:changed', { detail: { id: Number(val) } })); } catch (_) {}
                }}
                label={t('studentsDisciplinePage.filters.academicYear')}
              >
                {academicYears.map((year) => (
                  <MenuItem key={year.id} value={year.id}>
                    {year.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('studentsDisciplinePage.filters.sequence')}</InputLabel>
              <Select
                value={selectedSequenceNo}
                onChange={(e) => setSelectedSequenceNo(e.target.value)}
                label={t('studentsDisciplinePage.filters.sequence')}
                disabled={sequenceWindows.length === 0}
                renderValue={(value) => {
                  const v = String(value || '').trim();
                  if (!v) return sequenceWindows.length ? t('studentsDisciplinePage.filters.selectSequence') : t('studentsDisciplinePage.filters.noSequences');
                  return t('studentsDisciplinePage.filters.sequenceLabel', { number: v });
                }}
              >
                {sequenceWindows.length > 0 && (
                  <MenuItem value="" disabled>
                    {t('studentsDisciplinePage.filters.selectSequence')}
                  </MenuItem>
                )}
                {sequenceWindows.map((w) => (
                  <MenuItem key={w.id} value={String(w.sequenceNo)}>
                    {t('studentsDisciplinePage.filters.sequenceLabel', { number: w.sequenceNo })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('studentsDisciplinePage.filters.studentOptional')}</InputLabel>
              <Select
                value={searchStudentId}
                onChange={(e) => setSearchStudentId(e.target.value)}
                label={t('studentsDisciplinePage.filters.studentOptional')}
              >
                <MenuItem value="">{t('studentsDisciplinePage.filters.allStudents')}</MenuItem>
                {students
                  .filter((student) => !selectedClass || String(student.classId) === String(selectedClass))
                  .map((student) => (
                    <MenuItem key={student.id} value={student.id}>
                      {student.user
                        ? `${student.user.firstName} ${student.user.lastName}`
                        : `${student.firstName || ''} ${student.lastName || ''}`.trim() || student.studentNumber}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              onClick={() => {
                if (selectedSequenceNo && (selectedClass || searchStudentId)) {
                  fetchDisciplineSummary();
                } else if (!selectedSequenceNo) {
                  setError(t('studentsDisciplinePage.errors.selectSequenceFirst'));
                }
              }}
              startIcon={<RefreshIcon />}
            >
              {t('common.refresh')}
            </Button>
          </Grid>
        </Grid>
      </Paper>



      {/* Summary Cards */}
      {summaryStats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('studentsDisciplinePage.summary.absencesTime')}
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {formatMinutes(summaryStats.totalAbsenceMinutes || 0, t)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('studentsDisciplinePage.summary.latenessTime')}
                </Typography>
                <Typography variant="h4" color="info.main">
                  {formatMinutes(summaryStats.totalLateMinutes || 0, t)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('studentsDisciplinePage.summary.warnings')}
                </Typography>
                <Typography variant="h4" color="error.main">
                  {summaryStats.totalWarnings || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('studentsDisciplinePage.summary.dismissals')}
                </Typography>
                <Typography variant="h4" color="error.main">
                  {summaryStats.totalExclusions || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Discipline Records Table */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t('studentsDisciplinePage.table.title')}
        </Typography>
        
        {disciplineRecords.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              {t('studentsDisciplinePage.table.noRecords')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
              sx={{ mt: 2 }}
            >
              {t('studentsDisciplinePage.actions.addFirstRecord')}
            </Button>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('studentsDisciplinePage.table.columns.student')}</TableCell>
                    <TableCell>{t('studentsDisciplinePage.table.columns.absences')}</TableCell>
                    {!isNurPriPhase && (
                      <TableCell>{t('studentsDisciplinePage.table.columns.lateness')}</TableCell>
                    )}
                    <TableCell>{t('studentsDisciplinePage.table.columns.warnings')}</TableCell>
                    {!isNurPriPhase && (
                      <TableCell>{t('studentsDisciplinePage.table.columns.suspensionDays')}</TableCell>
                    )}
                    <TableCell>{t('studentsDisciplinePage.table.columns.dismissal')}</TableCell>
                    <TableCell>{t('studentsDisciplinePage.table.columns.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {disciplineRecords.map((record) => (
                    <TableRow key={`${record.sequenceNo}-${record.studentId}`}>
                      <TableCell>
                        {record.studentName || t('studentsDisciplinePage.table.unknownStudent')}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="caption" display="block">
                            {t('studentsDisciplinePage.table.justified')}: {formatMinutes(record.justifiedAbsenceMinutes || 0, t)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {t('studentsDisciplinePage.table.unjustified')}: {formatMinutes(record.unjustifiedAbsenceMinutes || 0, t)}
                          </Typography>
                        </Box>
                      </TableCell>
                      {!isNurPriPhase && (
                        <TableCell>
                          <Box>
                            <Typography variant="caption" display="block">
                              {t('studentsDisciplinePage.table.justified')}: {formatMinutes(record?.manual?.justifiedLateness || 0, t)}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {t('studentsDisciplinePage.table.unjustified')}: {formatMinutes(
                                (record?.manual?.justifiedLateness != null || record?.manual?.unjustifiedLateness != null)
                                  ? (record?.manual?.unjustifiedLateness || 0)
                                  : (record?.lateMinutes || 0)
                              , t)}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        {record?.manual?.warnings || 0}
                      </TableCell>
                      {!isNurPriPhase && (
                        <TableCell>
                          {computeSuspensionDays({
                            suspensionStartDate: record?.manual?.suspensionStartDate,
                            suspensionEndDate: record?.manual?.suspensionEndDate,
                            suspension: record?.manual?.suspension
                          })}
                        </TableCell>
                      )}
                      <TableCell>
                        {(record?.manual?.exclusion || false) ? t('common.yes') : t('common.no')}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title={t('studentsDisciplinePage.tooltips.viewDetails')}>
                            <IconButton size="small" onClick={() => handleOpenViewDialog(record)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('studentsDisciplinePage.tooltips.editRecord')}>
                            <IconButton size="small" onClick={() => handleOpenDialog('edit', record)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('studentsDisciplinePage.tooltips.deleteRecord')}>
                            <IconButton 
                              size="small" 
                              color="error"
                              disabled={!record.manualRecordId}
                              onClick={() => {
                                setRecordToDelete(record);
                                setDeleteConfirmOpen(true);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? t('studentsDisciplinePage.dialogs.form.addTitle') : t('studentsDisciplinePage.dialogs.form.editTitle')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Class Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('studentsDisciplinePage.filters.class')}</InputLabel>
                <Select
                  value={formData.classId || selectedClass}
                  onChange={(e) => {
                    setFormData({ ...formData, classId: e.target.value, studentId: '' });
                    setSelectedClass(e.target.value);
                  }}
                  label={t('studentsDisciplinePage.filters.class')}
                >
                  {classes.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>
                      {cls.name}{cls.section ? ` ${cls.section}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Student Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('studentsDisciplinePage.filters.student')}</InputLabel>
                <Select
                  value={formData.studentId}
                  onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                  label={t('studentsDisciplinePage.filters.student')}
                  disabled={!formData.classId}
                >
                  {students
                    .filter(student => String(student.classId) === String(formData.classId || selectedClass))
                    .map((student) => (
                      <MenuItem key={student.id} value={student.id}>
                        {student.user
                          ? `${student.user.firstName} ${student.user.lastName}`
                          : `${student.firstName || ''} ${student.lastName || ''}`.trim() || student.studentNumber}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

             {/* Manual Discipline Fields */}
             <Grid item xs={12} md={6}>
               <TextField
                 fullWidth
                 label={t('studentsDisciplinePage.form.justifiedAbsence')}
                 value={formData.justifiedAbsenceMinutes}
                 onChange={(e) => {
                   const nextVal = e.target.value;
                   const rebalanced = dialogMode === 'edit'
                     ? rebalanceMinutesPair({ total: editTotals.absTotal, nextValueHHMM: nextVal, side: 'justified' })
                     : null;
                   if (rebalanced) {
                     setFormData({
                       ...formData,
                       justifiedAbsenceMinutes: rebalanced.justified,
                       unjustifiedAbsenceMinutes: rebalanced.unjustified
                     });
                     return;
                   }
                   setFormData({ ...formData, justifiedAbsenceMinutes: nextVal });
                 }}
               />
             </Grid>

             <Grid item xs={12} md={6}>
               <TextField
                 fullWidth
                 label={t('studentsDisciplinePage.form.unjustifiedAbsence')}
                 value={formData.unjustifiedAbsenceMinutes}
                 onChange={(e) => {
                   const nextVal = e.target.value;
                   const rebalanced = dialogMode === 'edit'
                     ? rebalanceMinutesPair({ total: editTotals.absTotal, nextValueHHMM: nextVal, side: 'unjustified' })
                     : null;
                   if (rebalanced) {
                     setFormData({
                       ...formData,
                       justifiedAbsenceMinutes: rebalanced.justified,
                       unjustifiedAbsenceMinutes: rebalanced.unjustified
                     });
                     return;
                   }
                   setFormData({ ...formData, unjustifiedAbsenceMinutes: nextVal });
                 }}
               />
             </Grid>

             {!isNurPriPhase && (
               <Grid item xs={12} md={6}>
                 <TextField
                   fullWidth
                   label={t('studentsDisciplinePage.form.justifiedLateness')}
                   value={formData.justifiedLatenessMinutes}
                   onChange={(e) => {
                     const nextVal = e.target.value;
                     const rebalanced = dialogMode === 'edit'
                       ? rebalanceMinutesPair({ total: editTotals.lateTotal, nextValueHHMM: nextVal, side: 'justified' })
                       : null;
                     if (rebalanced) {
                       setFormData({
                         ...formData,
                         justifiedLatenessMinutes: rebalanced.justified,
                         unjustifiedLatenessMinutes: rebalanced.unjustified
                       });
                       return;
                     }
                     setFormData({ ...formData, justifiedLatenessMinutes: nextVal });
                   }}
                 />
               </Grid>
             )}

             {!isNurPriPhase && (
               <Grid item xs={12} md={6}>
                 <TextField
                   fullWidth
                   label={t('studentsDisciplinePage.form.unjustifiedLateness')}
                   value={formData.unjustifiedLatenessMinutes}
                   onChange={(e) => {
                     const nextVal = e.target.value;
                     const rebalanced = dialogMode === 'edit'
                       ? rebalanceMinutesPair({ total: editTotals.lateTotal, nextValueHHMM: nextVal, side: 'unjustified' })
                       : null;
                     if (rebalanced) {
                       setFormData({
                         ...formData,
                         justifiedLatenessMinutes: rebalanced.justified,
                         unjustifiedLatenessMinutes: rebalanced.unjustified
                       });
                       return;
                     }
                     setFormData({ ...formData, unjustifiedLatenessMinutes: nextVal });
                   }}
                 />
               </Grid>
             )}

             <Grid item xs={12} md={6}>
               <TextField
                 fullWidth
                 type="number"
                 label={t('studentsDisciplinePage.form.warnings')}
                 value={formData.warnings}
                 onChange={(e) => setFormData({ ...formData, warnings: parseInt(e.target.value) || 0 })}
                 inputProps={{ min: 0 }}
               />
             </Grid>
             {!isNurPriPhase && (
               <Grid item xs={12} md={6}>
                 <FormControlLabel
                   control={
                     <Switch
                       checked={formData.suspensionEnabled}
                       onChange={(e) =>
                         setFormData({
                           ...formData,
                           suspensionEnabled: e.target.checked,
                           suspensionStartDate: e.target.checked ? formData.suspensionStartDate : '',
                           suspensionEndDate: e.target.checked ? formData.suspensionEndDate : ''
                         })
                       }
                     />
                   }
                   label={t('studentsDisciplinePage.form.suspension')}
                 />
               </Grid>
             )}

             {!isNurPriPhase && formData.suspensionEnabled && (
               <>
                 <Grid item xs={12} md={6}>
                   <TextField
                     fullWidth
                     type="date"
                     label={t('studentsDisciplinePage.form.suspensionStartDate')}
                     value={formData.suspensionStartDate}
                     onChange={(e) => setFormData({ ...formData, suspensionStartDate: e.target.value })}
                     InputLabelProps={{ shrink: true }}
                   />
                 </Grid>

                 <Grid item xs={12} md={6}>
                   <TextField
                     fullWidth
                     type="date"
                     label={t('studentsDisciplinePage.form.suspensionEndDate')}
                     value={formData.suspensionEndDate}
                     onChange={(e) => setFormData({ ...formData, suspensionEndDate: e.target.value })}
                     InputLabelProps={{ shrink: true }}
                   />
                 </Grid>
               </>
             )}
             <Grid item xs={12} md={6}>
               <FormControlLabel
                 control={
                   <Switch
                     checked={formData.dismissal}
                     onChange={(e) => setFormData({ ...formData, dismissal: e.target.checked })}
                   />
                 }
                 label={t('studentsDisciplinePage.form.dismissal')}
               />
             </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'create' ? t('common.create') : t('common.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('studentsDisciplinePage.dialogs.details.title')}</DialogTitle>
        <DialogContent>
          {!viewRecord ? (
            <Typography>{t('studentsDisciplinePage.dialogs.details.noRecord')}</Typography>
          ) : (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {viewRecord.studentName || t('studentsDisciplinePage.dialogs.details.studentFallback')}
                </Typography>
                <Typography variant="body2">
                  {t('studentsDisciplinePage.dialogs.details.sequence')}: {viewRecord.sequenceNo ? t('studentsDisciplinePage.filters.sequenceLabel', { number: viewRecord.sequenceNo }) : t('common.na')}
                </Typography>
                <Typography variant="body2">
                  {t('studentsDisciplinePage.dialogs.details.absencesJU')}: {formatMinutes(viewRecord.justifiedAbsenceMinutes || 0, t)} / {formatMinutes(viewRecord.unjustifiedAbsenceMinutes || 0, t)}
                </Typography>
                {!isNurPriPhase && (
                  <Typography variant="body2">
                    {t('studentsDisciplinePage.dialogs.details.lateness')}: {formatMinutes(viewRecord.lateMinutes || 0, t)}
                  </Typography>
                )}
                <Typography variant="body2">
                  {t('studentsDisciplinePage.dialogs.details.manualAbsencesJU')}: {viewRecord?.manual?.justifiedAbsences || 0}/{viewRecord?.manual?.unjustifiedAbsences || 0}
                </Typography>
                {!isNurPriPhase && (
                  <Typography variant="body2">
                    {t('studentsDisciplinePage.dialogs.details.manualLatenessJU')}: {formatMinutes(viewRecord?.manual?.justifiedLateness || 0, t)}/{formatMinutes(viewRecord?.manual?.unjustifiedLateness || 0, t)}
                  </Typography>
                )}
                <Typography variant="body2">
                  {t('studentsDisciplinePage.dialogs.details.warnings')}: {viewRecord?.manual?.warnings || 0}
                </Typography>
                {!isNurPriPhase && (
                  <Typography variant="body2">
                    {t('studentsDisciplinePage.dialogs.details.suspensionDays')}: {computeSuspensionDays({
                      suspensionStartDate: viewRecord?.manual?.suspensionStartDate,
                      suspensionEndDate: viewRecord?.manual?.suspensionEndDate,
                      suspension: viewRecord?.manual?.suspension
                    })}
                  </Typography>
                )}
                <Typography variant="body2">
                  {t('studentsDisciplinePage.dialogs.details.dismissal')}: {(viewRecord?.manual?.exclusion || false) ? t('common.yes') : t('common.no')}
                </Typography>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('studentsDisciplinePage.dialogs.delete.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('studentsDisciplinePage.dialogs.delete.prompt')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentsDisciplinePage;
